import { Controller, useFormContext } from 'react-hook-form'

import { DropdownOption } from '@/entities/dropdowns/models/DropdownOption'
import { GridTable } from '@/shared/components/GridTable/GridTable'
import { MultiselectDropdown } from '@/shared/components/MultiselectDropdown/MultiselectDropdown'
import { pxToRem } from '@/shared/styles/themes'
import { Grid2 as Grid, Typography } from '@mui/material'
import { GridColDef } from '@mui/x-data-grid-premium'

type CompartmentsTabProps = {
  isReadOnly: boolean
  compartmentOptions: DropdownOption[]
  attributeOptions: DropdownOption[]
}

export const CompartmentsTab: React.FC<CompartmentsTabProps> = ({
  isReadOnly,
  compartmentOptions,
  attributeOptions,
}) => {
  const { watch, control } = useFormContext()

  // Retreive the value for compartmentDefinitions
  const rows = watch('compartmentIds') || []

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Compartment Name',
      width: 150,
      align: 'left',
      headerAlign: 'left',
      editable: false,
    },
    {
      field: 'volumeCapacity',
      headerName: 'Volume Capacity',
      width: 150,
      align: 'left',
      headerAlign: 'left',
      editable: false,
    },
    {
      field: 'weightCapacity',
      headerName: 'Weight Capacity',
      width: 150,
      align: 'left',
      headerAlign: 'left',
      editable: false,
    },
    {
      field: 'attributeIds',
      headerName: 'Attributes',
      width: 200,
      align: 'left',
      headerAlign: 'left',
      editable: false,
      valueGetter: (val: string[]) => {
        if (!val || val.length === 0) return ''

        return val.map((id) => {
          const attribute = attributeOptions.find((option) => option.value === id)

          return attribute ? attribute.label : ''
        }).join(', ')
      },
    },
  ]

  return (
    <Grid container spacing={1}>
      <Grid size={12}>
        <Typography
          variant="body1"
          sx={{
            fontSize: pxToRem(16),
            fontWeight: 500,
            mt: 1,
            mb: 1,
          }}
        >
          Compartments
        </Typography>
      </Grid>
      <Grid size={12}>
        <Controller
          name="compartmentIds"
          control={control}
          render={({ field, fieldState }) => (
            <>
              <MultiselectDropdown
                {...field}
                ref={field.ref}
                label="Compartments"
                placeholder="Select compartments"
                value={field.value}
                onChange={(_, value) => field.onChange(value)}
                options={compartmentOptions}
                getOptionLabel={(option) => {
                  return (option as DropdownOption).label
                }}
                isOptionEqualToValue={(option, selectedOption) => {
                  return (option as DropdownOption).value === (selectedOption as DropdownOption).value
                }}
                disablePortal={false}
                disabled={isReadOnly}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            </>
          )}
        />
      </Grid>
      <Grid size={12}>
        <GridTable
          columns={columns}
          rows={rows}
          hideFooter
        />
      </Grid>
    </Grid>
  )
}
